/**
 * RESPONSIBILITY: which site pages exist, and how a hash maps to one.
 *
 * Site pages, not framework screens. `isScreen()` already answers "is this login or
 * admin" and returns false for everything else — which is exactly the window this uses,
 * so a multi-page site needs nothing from the framework.
 *
 * An event is addressed by its row id and never by position: `#event/k3f9…` still points
 * at the same event after somebody reorders the list.
 */
export enum Page {
  Home = 'home',
  About = 'about',
  Children = 'children',
  Ministry = 'ministry',
  Events = 'events',
  Event = 'event',
  Sermons = 'sermons',
  Leadership = 'leadership',
  Give = 'give',
  HouseOfHonor = 'house-of-honor',
  Contact = 'contact',
  Visit = 'visit',
  Bulletin = 'bulletin',
  Adc = 'adc',
  Requests = 'requests',
}

export interface Route {
  page: Page;
  /**
   * Every segment after the page name, so a page may nest.
   *
   *   #events                      []
   *   #events/men                  ['men']
   *   #events/men/mens-breakfast   ['men', 'mens-breakfast']
   *
   * This used to destructure exactly two and drop the rest silently, which is why a
   * third segment did nothing at all rather than 404ing.
   */
  path: readonly string[];
}

const PAGES = new Set<string>(Object.values(Page));

/**
 * `#events` → Events. `#events/men/x` → Events with that trail. Anything else → `entry`,
 * which is Home unless the address itself named another page — see adc-entry.ts.
 */
export function routeOf(hash: string, entry: Page = Page.Home): Route {
  const [name, ...rest] = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  return PAGES.has(name) ? { page: name as Page, path: rest } : { page: entry, path: [] };
}

/** `hrefFor(Page.Events, 'men', 'mens-breakfast')` → `#events/men/mens-breakfast`. */
export const hrefFor = (page: Page, ...parts: (string | undefined)[]): string =>
  `#${page}${parts.filter(Boolean).map((p) => `/${p}`).join('')}`;
